import React, { useState } from 'react';
import { X, Plus, Copy, Check, Sparkles, Globe, Terminal } from 'lucide-react';
import { AppItem } from '../data/apps';

interface AddAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddApp: (newApp: AppItem) => void;
}

export const AddAppModal: React.FC<AddAppModalProps> = ({ isOpen, onClose, onAddApp }) => {
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<AppItem['category']>('Tools & Utilities');
  const [accent, setAccent] = useState<AppItem['accent']>('blue');
  const [tags, setTags] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);

  if (!isOpen) return null;

  const cleanSubdomain = subdomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
  const fullSubdomain = cleanSubdomain.endsWith('.tiger-home.com') 
    ? cleanSubdomain 
    : (cleanSubdomain ? `${cleanSubdomain}.tiger-home.com` : 'your-app.tiger-home.com');
  const fullUrl = `https://${fullSubdomain}/`;

  const generatedCode = `  {
    id: '${cleanSubdomain.replace('.tiger-home.com', '') || 'new-app'}',
    title: '${name.trim() || 'New Application'}',
    subdomain: '${fullSubdomain}',
    url: '${fullUrl}',
    description: '${description.trim() || 'Application description goes here.'}',
    category: '${category}',
    icon: 'box',
    badge: 'New App',
    accent: '${accent}',
    tags: [${tags.split(',').map(t => `'${t.trim()}'`).filter(t => t !== "''").join(', ') || "'Utilities'"}],
  },`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const newApp: AppItem = {
      id: cleanSubdomain.replace('.tiger-home.com', '') || `app-${Date.now()}`,
      title: name.trim(),
      subdomain: fullSubdomain,
      url: fullUrl,
      description: description.trim() || 'Newly connected Tiger Home application.',
      category,
      icon: 'box',
      badge: 'Live',
      accent,
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
    };

    onAddApp(newApp);
    onClose();
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
      <div className="w-full max-w-xl glass-card rounded-2xl border border-slate-800 shadow-2xl p-6 relative overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-tiger-500/10 border border-tiger-500/20 text-tiger-400 flex items-center justify-center">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Add New Subdomain App</h2>
              <p className="text-xs text-slate-400">Register a new app on the tiger-home.com domain</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="space-y-4 py-4 overflow-y-auto pr-1">
          <div>
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
              App Name
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Smart Weather Station"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-tiger-500 focus:ring-1 focus:ring-tiger-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Subdomain Prefix
              </label>
              <div className="flex items-center bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-sm text-white">
                <input
                  type="text"
                  required
                  placeholder="weather"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                  className="w-full bg-transparent focus:outline-none placeholder-slate-500 text-white"
                />
                <span className="text-xs text-slate-500 font-mono select-none">.tiger-home.com</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as any)}
                className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-tiger-500"
              >
                <option value="AI & Lifestyle">AI & Lifestyle</option>
                <option value="Hardware & Labs">Hardware & Labs</option>
                <option value="Pet & Health">Pet & Health</option>
                <option value="Tools & Utilities">Tools & Utilities</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
              Description
            </label>
            <textarea
              rows={2}
              placeholder="Brief description of what this app does..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3.5 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-tiger-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Color Accent
              </label>
              <div className="flex gap-2 pt-1">
                {(['emerald', 'cyan', 'purple', 'amber', 'blue'] as const).map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setAccent(color)}
                    className={`w-7 h-7 rounded-lg border-2 transition-all ${
                      color === 'emerald' ? 'bg-emerald-500' :
                      color === 'cyan' ? 'bg-cyan-500' :
                      color === 'purple' ? 'bg-purple-500' :
                      color === 'amber' ? 'bg-amber-500' : 'bg-blue-500'
                    } ${accent === color ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Tags (comma separated)
              </label>
              <input
                type="text"
                placeholder="IoT, ESP32, Dashboard"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-tiger-500"
              />
            </div>
          </div>

          {/* Code Snippet for persistent config */}
          <div className="p-3 bg-slate-950/80 rounded-xl border border-slate-800 text-xs">
            <div className="flex items-center justify-between text-slate-400 mb-1.5 font-mono">
              <span className="flex items-center gap-1.5 text-[11px]">
                <Terminal className="w-3.5 h-3.5 text-tiger-400" />
                Persistent Code for src/data/apps.ts:
              </span>
              <button
                type="button"
                onClick={handleCopyCode}
                className="inline-flex items-center gap-1 text-[11px] text-tiger-400 hover:text-tiger-300"
              >
                {copiedCode ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedCode ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            <pre className="text-slate-300 font-mono text-[10px] overflow-x-auto p-2 bg-slate-900/90 rounded border border-slate-800/60">
              {generatedCode}
            </pre>
          </div>

          {/* Action Buttons */}
          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-tiger-500 hover:bg-tiger-600 text-white font-semibold text-sm shadow-lg shadow-tiger-500/20 transition-all disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              <span>Add to Dashboard</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
